import React from 'react';

import { AddCircleOutline, Start } from '@mui/icons-material';
import type { DrawerProps } from '@mui/material';
import { Button, Drawer, IconButton, Stack, Toolbar, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { compact, first, isEmpty, isNil, last, map, set, size } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { ContainerFlexHeight } from '../../../../components';
import { createScopedI18n } from '../../../../i18n/i18n';
import JobDayCard from './job_bid_price_card';
import { MAX_SCHEDULE_DATE_LENGTH, MAX_SCHEDULE_DAYS, MIN_SCHEDULE_DAYS } from './page';

import type { JobCreatesSchemaType } from './page';

const jobFieldI18n = createScopedI18n('graphql.fields.job');
const jobCreatePageI18n = createScopedI18n('pages.jobs_new');

export type JobCardListProps = {
  open?: DrawerProps['open'];
  onClose?: DrawerProps['onClose'];
};

const JobCardListDrawer = ({ open, onClose }: JobCardListProps) => {
  const theme = useTheme();

  const { watch, setValue } = useFormContext<JobCreatesSchemaType>();

  const bidPrices = watch('bidPrices');
  const scheduleDates = watch('scheduleDates');

  const onCloseHandler = React.useCallback(
    (e: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
      onClose?.(e as {}, reason ?? 'backdropClick');
    },
    [onClose],
  );

  const canAddDate = React.useCallback(
    (date: dayjs.Dayjs | Date | null | undefined) => {
      if (isNil(date)) {
        return false;
      }

      if (size(scheduleDates) >= MAX_SCHEDULE_DATE_LENGTH) {
        return false;
      }

      const minDate = dayjs().startOf('day').add(MIN_SCHEDULE_DAYS, 'days');
      const maxDate = dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days');

      return dayjs(date).isAfter(minDate) && dayjs(date).isBefore(maxDate);
    },
    [scheduleDates],
  );

  const prependDate = React.useMemo(() => {
    const date = dayjs(first(scheduleDates)).subtract(1, 'day');
    return date;
  }, [scheduleDates]);

  const appendDate = React.useMemo(() => {
    const date = dayjs(last(scheduleDates)).add(1, 'day');
    return date;
  }, [scheduleDates]);

  const bidPriceUpdateHandlerHigherOrder = React.useCallback(
    (index: number) => (value: JobCreatesSchemaType['bidPrices'][0]) => {
      const nextBidPrices = set([...bidPrices], index, value);
      setValue('bidPrices', compact(nextBidPrices));
    },
    [bidPrices],
  );

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onCloseHandler}
      PaperProps={{ sx: { width: '100%', maxWidth: theme.breakpoints.values.sm } }}
    >
      <Toolbar>
        <IconButton size="large" edge="start" color="inherit" onClick={onCloseHandler}>
          <Start />
        </IconButton>
      </Toolbar>

      <ContainerFlexHeight sx={{ display: 'flex', flexDirection: 'column' }}>
        {isEmpty(bidPrices) ? (
          <Stack flex={1} justifyContent={'center'} alignItems={'center'}>
            <Button variant="outlined" onClick={onCloseHandler}>
              {jobFieldI18n('bid_price.detail')}
            </Button>
          </Stack>
        ) : (
          <Stack gap={2} padding={2} paddingBottom={8} flex={1}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={
                canAddDate(prependDate)
                  ? () => setValue('scheduleDates', [prependDate.toDate(), ...scheduleDates])
                  : undefined
              }
              disabled={!canAddDate(prependDate)}
            >
              {jobCreatePageI18n('add_day_before')}
            </Button>

            {map(bidPrices, (bidPrice, index) => (
              <JobDayCard
                key={bidPrice.scheduleDate}
                scheduleDate={bidPrice.scheduleDate}
                bidPrice={bidPrice}
                onUpdateBidPrice={bidPriceUpdateHandlerHigherOrder(index)}
              />
            ))}

            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={
                canAddDate(appendDate)
                  ? () => setValue('scheduleDates', [...scheduleDates, appendDate.toDate()])
                  : undefined
              }
              disabled={!canAddDate(appendDate)}
            >
              {jobCreatePageI18n('add_day_after')}
            </Button>
          </Stack>
        )}
      </ContainerFlexHeight>
    </Drawer>
  );
};

export default JobCardListDrawer;
